import React from 'react';
import {Box, Button, Paper, Stack, TextField, Typography} from '@mui/material';
import {SignerParams} from 'hooks/useSigningDemo';
import {TranslateKey, useTranslate} from 'hooks/useTranslate';

interface SignersEditorProps {
  signers: SignerParams[];
  onChange: (signers: SignerParams[]) => void;
  // Immediate signing: only the first signer is shown and none can be added.
  single?: boolean;
}

// The first signer is always present and cannot be removed: a session needs at least one.
export const DEFAULT_SIGNER_ID = 'signer-default';

let nextId = 0;
export const newSigner = (): SignerParams => ({
  id: `signer-${nextId++}`,
  governmentId: '',
  fullName: '',
  firstName: '',
  lastName: '',
});

const fields: Array<{
  key: keyof Omit<SignerParams, 'id'>;
  label: TranslateKey;
}> = [
  {key: 'governmentId', label: 'demo.signing.field.governmentId'},
  {key: 'fullName', label: 'demo.signing.field.fullName'},
  {key: 'firstName', label: 'demo.signing.field.firstName'},
  {key: 'lastName', label: 'demo.signing.field.lastName'},
];

export const SignersEditor = ({
  signers,
  onChange,
  single,
}: SignersEditorProps) => {
  const t = useTranslate();
  const update = (id: string, patch: Partial<SignerParams>) =>
    onChange(signers.map((s) => (s.id === id ? {...s, ...patch} : s)));

  return (
    <Stack spacing={2}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Typography variant="h6" component="h2" sx={{flex: 1}}>
          {t('demo.signing.signers.title')}
        </Typography>
        {!single && (
          <Button
            variant="outlined"
            onClick={() => onChange([...signers, newSigner()])}>
            {t('demo.signing.signers.add')}
          </Button>
        )}
      </Box>
      {(single ? signers.slice(0, 1) : signers).map((signer, index) => (
        <Paper key={signer.id} variant="outlined" sx={{p: 2}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Typography sx={{minWidth: 80}}>
              {t('demo.signing.signers.signer', {number: index + 1})}
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {xs: '1fr', md: 'repeat(4, 1fr)'},
              }}>
              {fields.map(({key, label}) => (
                <TextField
                  key={key}
                  size="small"
                  label={t(label)}
                  value={signer[key]}
                  onChange={(e) => update(signer.id, {[key]: e.target.value})}
                  slotProps={{
                    htmlInput: {'aria-label': `${t(label)} ${index + 1}`},
                  }}
                />
              ))}
            </Box>
            <Button
              color="error"
              disabled={signer.id === DEFAULT_SIGNER_ID}
              aria-label={t('demo.signing.signers.removeAria', {
                number: index + 1,
              })}
              onClick={() =>
                onChange(signers.filter((s) => s.id !== signer.id))
              }>
              {t('demo.signing.signers.remove')}
            </Button>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
};
